import {
  DETERMINERS,
  formsOf,
  isAuxiliary,
  MID_ADVERBS,
} from "@/lib/tool-kit/style-checkers/a-lexicon";
import { type Word, wordsIn } from "@/lib/tool-kit/style-checkers/a-words";
import {
  countWords,
  headingFlags,
  insideAny,
  quotedSpans,
  type Sentence,
  type Span,
  splitSentences,
  type Tally,
  tally,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * Pronouns that point at nothing in particular: "This shows…", "…, which suggests…",
 * "It was…" opening a paragraph.
 *
 * A pronoun is vague when the reader cannot name its antecedent without guessing. That is
 * a judgement about meaning, so this module flags only the patterns that make guessing
 * likely and are cheap to recognise:
 *
 * - a sentence that opens with This, These, That or Those standing alone, followed by a
 *   verb ("This limits…") — a determiner use ("This study…") is fine and is not flagged;
 * - "this" or "these" alone after a conjunction ("…, and this reduced accuracy");
 * - ", which" followed by a verb that comments on the whole preceding clause
 *   ("…small, which limits…"), unless a closing comma shows it belongs to a noun;
 * - a sentence that opens with "Which" and is not a question;
 * - "It" opening the first sentence of a paragraph, where nothing precedes it.
 *
 * It does not decide which of two nouns "it" means; that needs the sentence's meaning.
 */

export type VagueKind =
  | "demonstrative-start"
  | "demonstrative-mid"
  | "which-broad"
  | "which-start"
  | "it-first";

export type VagueConfidence = "likely" | "possible";

export interface VagueMatch extends Span {
  text: string;
  /** Lowercase: this, these, that, those, which or it. */
  pronoun: string;
  kind: VagueKind;
  confidence: VagueConfidence;
  /** The verb that follows, lowercase, when there is one. */
  verb: string | null;
  sentenceStart: number;
  sentence: string;
  /** Rewrites of the opening words: "This finding shows", "These results show". */
  ideas: string[];
}

export interface VagueReport {
  words: number;
  sentences: number;
  matches: VagueMatch[];
  likely: number;
  possible: number;
  byPronoun: Tally[];
  byKind: Record<VagueKind, number>;
  /** Sentences that open with a pronoun-use of this, these, that or those. */
  sentenceInitial: number;
}

/* ------------------------------------------------------------------ word lists */

/** Verbs that very often follow a stray "this", "these" or ", which". */
const REFERENCE_BASES = [
  "show",
  "suggest",
  "indicate",
  "mean",
  "imply",
  "demonstrate",
  "represent",
  "include",
  "make",
  "cause",
  "lead",
  "reflect",
  "support",
  "contradict",
  "confirm",
  "highlight",
  "raise",
  "reveal",
  "explain",
  "point",
  "remain",
  "seem",
  "appear",
  "help",
  "allow",
  "require",
  "provide",
  "create",
  "produce",
  "reduce",
  "increase",
  "limit",
  "affect",
  "change",
  "complicate",
  "undermine",
  "strengthen",
  "weaken",
  "justify",
  "prove",
  "illustrate",
  "give",
  "bring",
  "result",
  "happen",
  "occur",
  "matter",
  "fail",
  "prevent",
  "enable",
  "encourage",
  "constitute",
  "reinforce",
  "persist",
  "emerge",
  "vary",
  "differ",
  "depend",
  "relate",
  "follow",
  "stem",
  "arise",
  "force",
  "leave",
  "remind",
  "surprise",
  "worry",
  "concern",
  "suffice",
  "need",
  "work",
];

const REFERENCE_FORMS = formsOf(REFERENCE_BASES);
const REFERENCE_BASE_SET: ReadonlySet<string> = new Set(REFERENCE_BASES);

/** Forms that fit a singular pronoun ("shows", "showed"): the verb forms that are not bare. */
function singularForms(): Set<string> {
  const out = new Set<string>();
  for (const form of REFERENCE_FORMS) {
    if (!REFERENCE_BASE_SET.has(form)) {
      out.add(form);
    }
  }
  return out;
}

/** Forms that fit a plural pronoun ("show", "showed"): bare and past, not the -s form. */
function pluralForms(): Set<string> {
  const out = new Set<string>(REFERENCE_BASE_SET);
  for (const form of REFERENCE_FORMS) {
    if (!form.endsWith("s")) {
      out.add(form);
    }
  }
  return out;
}

const SINGULAR_VERBS = singularForms();
const PLURAL_VERBS = pluralForms();

const COPULAS: ReadonlySet<string> = new Set([
  "is",
  "was",
  "are",
  "were",
  "will",
  "would",
  "can",
  "could",
  "may",
  "might",
  "must",
  "should",
  "has",
  "had",
  "have",
  "does",
  "did",
  "do",
]);

const SINGULAR_DEMONSTRATIVES: ReadonlySet<string> = new Set(["this", "that"]);
const DEMONSTRATIVES: ReadonlySet<string> = new Set([
  "this",
  "that",
  "these",
  "those",
]);

/** "This, however, …" and "That said, …": punctuation or an idiom, not a missing noun. */
const IDIOMS: ReadonlySet<string> = new Set(["that said", "that is"]);

const MID_PREVIOUS: ReadonlySet<string> = new Set([
  "and",
  "but",
  "so",
  "because",
  "since",
  "while",
  "although",
  "though",
  "if",
  "when",
  "as",
  "yet",
  "or",
  "whereas",
]);

/** Verbs after ", which" that comment on the whole clause before it. */
const WHICH_VERBS: ReadonlySet<string> = new Set([
  ...formsOf(REFERENCE_BASES.filter((verb) => verb !== "work")),
]);

const WHICH_MODALS: ReadonlySet<string> = new Set([
  "would",
  "could",
  "may",
  "might",
  "can",
  "will",
  "should",
  "must",
]);

const WHICH_COPULAS: ReadonlySet<string> = new Set([
  "is",
  "was",
  "seems",
  "remains",
  "became",
  "becomes",
]);

/** After "which is": an opinion about the whole preceding clause. */
const EVALUATIVE: ReadonlySet<string> = new Set([
  "surprising",
  "unclear",
  "important",
  "concerning",
  "worrying",
  "unfortunate",
  "interesting",
  "notable",
  "remarkable",
  "ironic",
  "problematic",
  "troubling",
  "striking",
  "understandable",
  "unsurprising",
  "encouraging",
  "puzzling",
  "likely",
  "possible",
  "unlikely",
]);

/** The word after "it" that makes it a dummy subject, so there is no antecedent to find. */
const DUMMY_IT_VERBS: ReadonlySet<string> = new Set([
  "seems",
  "seem",
  "appears",
  "appear",
  "happens",
  "follows",
  "turns",
  "rains",
  "snows",
  "rained",
  "snowed",
  "poured",
  "takes",
  "depends",
  "matters",
  "looks",
  "remains",
  "stands",
  "goes",
  "works",
]);

const DUMMY_COMPLEMENTS: ReadonlySet<string> = new Set([
  "important",
  "essential",
  "necessary",
  "possible",
  "impossible",
  "likely",
  "unlikely",
  "clear",
  "evident",
  "obvious",
  "apparent",
  "true",
  "false",
  "difficult",
  "hard",
  "easy",
  "worth",
  "vital",
  "critical",
  "crucial",
  "known",
  "believed",
  "argued",
  "shown",
  "found",
  "said",
  "thought",
  "suggested",
  "recommended",
  "time",
  "raining",
  "snowing",
  "sunny",
  "cloudy",
  "windy",
  "late",
  "early",
  "noon",
  "midnight",
  "dark",
  "cold",
  "hot",
  "warm",
]);

const BE_FORMS: ReadonlySet<string> = new Set([
  "is",
  "was",
  "were",
  "are",
  "be",
  "been",
  "'s",
  "has",
  "have",
  "had",
  "will",
  "would",
  "isn't",
  "wasn't",
]);

const HAS_AUXILIARIES: ReadonlySet<string> = new Set([
  "has",
  "have",
  "had",
  "will",
  "would",
]);

const DUMMY_FOLLOWERS: ReadonlySet<string> = new Set([
  "that",
  "to",
  "whether",
  "if",
  "when",
  "how",
]);

const RE_LY = /^\p{L}{3,}ly$/u;
const RE_PUNCTUATION_NEXT = /^\s*[,;:.!?)]/;
const ALL_REFERENCE_FORMS = formsOf(REFERENCE_BASES);
const RE_PRECEDING_COMMA = /,\s*$/;
const RE_TERMINAL_QUESTION = /\?["'”’)\]]*$/;

/* ------------------------------------------------------------------ noun ideas */

interface NounIdeas {
  singular: readonly string[];
  plural: readonly string[];
}

const EVIDENCE_NOUNS: NounIdeas = {
  singular: ["finding", "result", "pattern"],
  plural: ["findings", "results", "patterns"],
};
const CHANGE_NOUNS: NounIdeas = {
  singular: ["change", "trend", "decision"],
  plural: ["changes", "trends", "decisions"],
};
const GENERAL_NOUNS: NounIdeas = {
  singular: ["finding", "approach", "problem"],
  plural: ["findings", "approaches", "problems"],
};

const EVIDENCE_VERBS = formsOf([
  "show",
  "suggest",
  "indicate",
  "imply",
  "demonstrate",
  "prove",
  "confirm",
  "reveal",
  "illustrate",
  "highlight",
  "mean",
  "contradict",
  "reflect",
  "support",
]);
const CHANGE_VERBS = formsOf([
  "cause",
  "lead",
  "make",
  "increase",
  "reduce",
  "limit",
  "affect",
  "change",
  "prevent",
  "enable",
  "allow",
  "help",
  "result",
  "complicate",
  "undermine",
  "strengthen",
  "weaken",
]);

function nounIdeasFor(verb: string | null): NounIdeas {
  if (verb && EVIDENCE_VERBS.has(verb)) {
    return EVIDENCE_NOUNS;
  }
  if (verb && CHANGE_VERBS.has(verb)) {
    return CHANGE_NOUNS;
  }
  return GENERAL_NOUNS;
}

function ideasFor(
  pronoun: string,
  word: string,
  verb: string | null,
  plural: boolean
): string[] {
  const nouns = nounIdeasFor(verb);
  const list = plural ? nouns.plural : nouns.singular;
  const tail = verb ? ` ${verb}` : "";
  if (pronoun === "which") {
    return list.slice(0, 3).map((noun) => `This ${noun}${tail}`);
  }
  return list.slice(0, 3).map((noun) => `${word} ${noun}${tail}`);
}

/* ------------------------------------------------------------------ pronoun use */

/** The first word after `index` that is not an adverb, and its position. */
function nextContent(
  tokens: readonly Word[],
  index: number
): { word: Word; at: number } | null {
  let at = index + 1;
  while (at < tokens.length && at <= index + 3) {
    const lower = tokens[at].lower;
    if (!(MID_ADVERBS.has(lower) || RE_LY.test(lower))) {
      break;
    }
    at++;
  }
  return at < tokens.length ? { word: tokens[at], at } : null;
}

const COPULA_FORMS: ReadonlySet<string> = new Set(["is", "was", "are", "were"]);

/**
 * What follows "This is…" when it comments on something earlier: a reason ("why",
 * "because") or a judgement ("important", "unlikely"). Anything else ("This is the
 * official example", "These are real articles") is taken to name or introduce a thing,
 * so nothing is left for the reader to guess.
 */
const COMMENTING: ReadonlySet<string> = new Set([
  "why",
  "because",
  "due",
  "important",
  "essential",
  "necessary",
  "critical",
  "crucial",
  "significant",
  "clear",
  "evident",
  "true",
  "false",
  "correct",
  "wrong",
  "right",
  "fine",
  "good",
  "bad",
  "difficult",
  "hard",
  "easy",
  "likely",
  "unlikely",
  "possible",
  "impossible",
  "consistent",
  "problematic",
  "surprising",
  "unclear",
  "worrying",
  "concerning",
  "unfortunate",
  "interesting",
  "notable",
  "remarkable",
  "ironic",
  "troubling",
  "striking",
  "understandable",
  "encouraging",
  "puzzling",
  "worth",
]);

function comments(tokens: readonly Word[], copula: number): boolean {
  const next = nextContent(tokens, copula);
  return next ? COMMENTING.has(next.word.lower) : false;
}

/**
 * Whether a demonstrative at `index` stands alone: followed by punctuation, the end of the
 * sentence, an auxiliary, or a verb that fits its number. "This study" and "These results
 * show" are determiner uses and return false.
 */
function standsAlone(
  text: string,
  tokens: readonly Word[],
  index: number
): { alone: boolean; verb: string | null } {
  const token = tokens[index];
  if (
    RE_PUNCTUATION_NEXT.test(text.slice(token.end, token.end + 3)) ||
    index === tokens.length - 1
  ) {
    return { alone: true, verb: null };
  }
  const next = nextContent(tokens, index);
  if (!next) {
    return { alone: true, verb: null };
  }
  const lower = next.word.lower;
  if (COPULA_FORMS.has(lower)) {
    return comments(tokens, next.at)
      ? { alone: true, verb: lower }
      : { alone: false, verb: null };
  }
  if (isAuxiliary(lower)) {
    return { alone: true, verb: lower };
  }
  const forms = SINGULAR_DEMONSTRATIVES.has(token.lower)
    ? SINGULAR_VERBS
    : PLURAL_VERBS;
  return forms.has(lower)
    ? { alone: true, verb: lower }
    : { alone: false, verb: null };
}

function isDummyIt(tokens: readonly Word[], index: number): boolean {
  const next = nextContent(tokens, index);
  if (!next) {
    return false;
  }
  const lower = next.word.lower;
  if (DUMMY_IT_VERBS.has(lower)) {
    return true;
  }
  if (!BE_FORMS.has(lower)) {
    return false;
  }
  let at = next.at + 1;
  while (
    at < tokens.length &&
    (tokens[at].lower === "been" ||
      tokens[at].lower === "be" ||
      MID_ADVERBS.has(tokens[at].lower))
  ) {
    at++;
  }
  // "had flaws" is possession, not "had been": an auxiliary needs a be after it.
  if (
    HAS_AUXILIARIES.has(lower) &&
    !tokens
      .slice(next.at + 1, at)
      .some((w) => w.lower === "been" || w.lower === "be")
  ) {
    return false;
  }
  const complement = tokens[at]?.lower ?? "";
  if (DUMMY_COMPLEMENTS.has(complement)) {
    return true;
  }
  return tokens
    .slice(at, at + 5)
    .some((word) => DUMMY_FOLLOWERS.has(word.lower));
}

function confidenceFor(verb: string | null, pronoun: string): VagueConfidence {
  if (pronoun === "that" || pronoun === "those") {
    return "possible";
  }
  return verb && COPULAS.has(verb) ? "possible" : "likely";
}

/* ------------------------------------------------------------------ scanning */

interface Context {
  text: string;
  quotes: readonly Span[];
  sentence: Sentence;
  tokens: readonly Word[];
  paragraphFirst: boolean;
  firstOfText: boolean;
}

function build(
  context: Context,
  word: Word,
  kind: VagueKind,
  confidence: VagueConfidence,
  verb: string | null
): VagueMatch {
  const pronoun = word.lower;
  const plural = pronoun === "these" || pronoun === "those";
  const wantsNoun =
    kind === "demonstrative-start" ||
    kind === "demonstrative-mid" ||
    kind === "which-broad" ||
    kind === "which-start";
  return {
    start: word.start,
    end: word.end,
    text: word.text,
    pronoun,
    kind,
    confidence,
    verb,
    sentenceStart: context.sentence.start,
    sentence: context.sentence.text,
    ideas: wantsNoun ? ideasFor(pronoun, word.text, verb, plural) : [],
  };
}

function scanDemonstrative(context: Context, index: number): VagueMatch | null {
  const { text, tokens } = context;
  const word = tokens[index];
  if (!DEMONSTRATIVES.has(word.lower)) {
    return null;
  }
  const atStart = index === 0;
  const previous = tokens[index - 1]?.lower ?? "";
  const afterBreak = RE_PRECEDING_COMMA.test(
    text.slice(Math.max(0, word.start - 3), word.start)
  );
  const mid =
    !atStart &&
    word.lower !== "that" &&
    word.lower !== "those" &&
    (MID_PREVIOUS.has(previous) || afterBreak);
  if (!(atStart || mid)) {
    return null;
  }
  const { alone, verb } = standsAlone(text, tokens, index);
  if (
    !alone ||
    IDIOMS.has(`${word.lower} ${verb ?? tokens[index + 1]?.lower ?? ""}`)
  ) {
    return null;
  }
  const kind = atStart ? "demonstrative-start" : "demonstrative-mid";
  const confidence = atStart ? confidenceFor(verb, word.lower) : "possible";
  return build(context, word, kind, confidence, verb);
}

/** A determiner in the three words before the comma suggests "which" follows a noun phrase. */
function endsInNounPhrase(tokens: readonly Word[], index: number): boolean {
  return tokens
    .slice(Math.max(0, index - 3), index)
    .some((word) => DETERMINERS.has(word.lower));
}

function whichConfidence(
  tokens: readonly Word[],
  index: number,
  atStart = false
): { verb: string; confidence: VagueConfidence } | null {
  const next = nextContent(tokens, index);
  if (!next) {
    return null;
  }
  const verb = next.word.lower;
  if (WHICH_VERBS.has(verb)) {
    return {
      verb,
      confidence: endsInNounPhrase(tokens, index) ? "possible" : "likely",
    };
  }
  if (WHICH_MODALS.has(verb)) {
    return { verb, confidence: "possible" };
  }
  if (WHICH_COPULAS.has(verb)) {
    const after = tokens[next.at + 1]?.lower ?? "";
    const following = MID_ADVERBS.has(after)
      ? (tokens[next.at + 2]?.lower ?? "")
      : after;
    const connective =
      atStart && (following === "why" || following === "because");
    return EVALUATIVE.has(following) || connective
      ? { verb, confidence: "possible" }
      : null;
  }
  return null;
}

/** A closing comma followed by a verb means the clause belongs to a noun: "The model, which fits, is…". */
function closedByComma(context: Context, index: number): boolean {
  const rest = context.tokens.slice(index + 1);
  const comma = context.text.indexOf(",", context.tokens[index].end);
  if (comma === -1 || comma >= context.sentence.end) {
    return false;
  }
  const after = rest.find((w) => w.start > comma);
  return after
    ? isAuxiliary(after.lower) || ALL_REFERENCE_FORMS.has(after.lower)
    : false;
}

function scanWhich(context: Context, index: number): VagueMatch | null {
  const { text, tokens, sentence } = context;
  const word = tokens[index];
  if (word.lower !== "which") {
    return null;
  }
  if (index === 0) {
    const question = RE_TERMINAL_QUESTION.test(sentence.text);
    const found = whichConfidence(tokens, index, true);
    return question || !found
      ? null
      : build(context, word, "which-start", "likely", found.verb);
  }
  if (
    !RE_PRECEDING_COMMA.test(
      text.slice(Math.max(0, word.start - 3), word.start)
    )
  ) {
    return null;
  }
  const found = whichConfidence(tokens, index);
  if (!found || closedByComma(context, index)) {
    return null;
  }
  return build(context, word, "which-broad", found.confidence, found.verb);
}

function scanIt(context: Context, index: number): VagueMatch | null {
  const word = context.tokens[index];
  if (
    word.lower !== "it" ||
    index !== 0 ||
    !context.paragraphFirst ||
    isDummyIt(context.tokens, index)
  ) {
    return null;
  }
  return build(
    context,
    word,
    "it-first",
    context.firstOfText ? "possible" : "likely",
    null
  );
}

function scanSentence(context: Context): VagueMatch[] {
  const found: VagueMatch[] = [];
  for (let index = 0; index < context.tokens.length; index++) {
    const word = context.tokens[index];
    if (insideAny(context.quotes, word.start, word.end)) {
      continue;
    }
    const match =
      scanDemonstrative(context, index) ??
      scanWhich(context, index) ??
      scanIt(context, index);
    if (match) {
      found.push(match);
    }
  }
  return found;
}

/* ------------------------------------------------------------------ report */

export interface VagueOptions {
  /** Skip pronouns inside quotation marks. Default true. */
  ignoreQuotes?: boolean;
}

const KINDS: readonly VagueKind[] = [
  "demonstrative-start",
  "demonstrative-mid",
  "which-broad",
  "which-start",
  "it-first",
];

function startsParagraph(
  text: string,
  sentences: readonly Sentence[],
  headings: readonly boolean[],
  i: number
): boolean {
  if (i === 0 || headings[i - 1]) {
    return true;
  }
  return text.slice(sentences[i - 1].end, sentences[i].start).includes("\n");
}

export function findVaguePronouns(
  text: string,
  options: VagueOptions = {}
): VagueReport {
  const quotes = options.ignoreQuotes === false ? [] : quotedSpans(text);
  const sentences = splitSentences(text);
  const headings = headingFlags(sentences);
  const matches: VagueMatch[] = [];
  let counted = 0;
  for (const [i, sentence] of sentences.entries()) {
    if (headings[i]) {
      continue;
    }
    counted++;
    matches.push(
      ...scanSentence({
        text,
        quotes,
        sentence,
        tokens: wordsIn(text, sentence.start, sentence.end),
        paragraphFirst: startsParagraph(text, sentences, headings, i),
        firstOfText: i === 0,
      })
    );
  }
  const byKind = Object.fromEntries(KINDS.map((kind) => [kind, 0])) as Record<
    VagueKind,
    number
  >;
  for (const match of matches) {
    byKind[match.kind]++;
  }
  const likely = matches.filter((m) => m.confidence === "likely").length;
  return {
    words: countWords(text),
    sentences: counted,
    matches,
    likely,
    possible: matches.length - likely,
    byPronoun: tally(matches.map((m) => m.pronoun)),
    byKind,
    sentenceInitial: byKind["demonstrative-start"],
  };
}
